import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Modal, StatusBadge, TipoBadge, CategoriaBadge } from "@/components/ui-bits";
import { toastCancel, toastReschedule, toastPrenatal } from "@/lib/toasts";
import { paymentConfirmedLink } from "@/lib/constants";
import InstallPWA from "@/components/InstallPWA";
import NotificationToggle from "@/components/NotificationToggle";
import {
  todayStr,
  addDaysStr,
  fmtDateLong,
  fmtDateShort,
  DAYS,
  DURATIONS,
  ROLE_LABEL,
  STATUS_META,
  waLink,
  confirmMsg,
  reminderMsg,
  clinicNotifyLink,
  prenatalInviteLink,
  CATEGORIAS,
  CATEGORIA_META,
} from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip,
} from "recharts";
import {
  CalendarDays, BarChart3, Settings, Users, Bell, LogOut, ChevronLeft, ChevronRight,
  Plus, Loader2, Phone, MessageCircle, RefreshCw, CalendarX, Baby, Clock, User as UserIcon,
  Trash2, CheckCircle2, X, Search, NotebookPen, CalendarOff, AlarmClock, Send, Receipt, Copy, Upload,
} from "lucide-react";

const STATUS_ACTIONS = [
  { key: "confirmada", label: "Confirmar" },
  { key: "asistio", label: "Asistió" },
  { key: "no_asistio", label: "No asistió" },
  { key: "cancelada", label: "Cancelar" },
];

/* ---------------- Appointment Detail ---------------- */
function ApptDetail({ appt, open, onClose, onChanged, canNotes }) {
  const [notes, setNotes] = useState(appt?.internal_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [reDate, setReDate] = useState(appt?.date);
  const [reSlots, setReSlots] = useState([]);
  const [reTime, setReTime] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);

  useEffect(() => {
    setNotes(appt?.internal_notes || "");
    setReDate(appt?.date);
    setShowReschedule(false);
  }, [appt]);

  useEffect(() => {
    if (!showReschedule) return;
    api.get("/availability", { params: { date: reDate } }).then((r) => setReSlots(r.data.slots)).catch((e) => console.error("Error al cargar horarios:", e));
  }, [reDate, showReschedule]);

  const setStatus = async (status) => {
    try {
      await api.put(`/appointments/${appt.id}`, { status });
      if (status === "confirmada") {
        // WhatsApp asistido: abrir confirmación prellenada al paciente
        window.open(
          waLink(appt.patient_phone, confirmMsg(appt.patient_name, appt.date, appt.time)),
          "_blank"
        );
        api.put(`/appointments/${appt.id}/mark-sent`, null, { params: { kind: "confirm" } }).catch((error) => console.error("No se pudo marcar confirmación enviada:", error));
        toast.success("Cita confirmada · abriendo WhatsApp");
      } else if (status === "cancelada") {
        toastCancel("Cita cancelada");
      } else {
        toast.success("Estado actualizado");
      }
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/appointments/${appt.id}`, { internal_notes: notes });
      toast.success("Nota guardada");
      onChanged();
    } catch (error) {
      console.error("Error al guardar nota:", error);
      toast.error("Error al guardar nota");
    } finally {
      setSavingNotes(false);
    }
  };

  const deleteAppt = async () => {
    if (!window.confirm("¿Borrar esta cita de forma permanente? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/appointments/${appt.id}/permanent`);
      toast.success("Cita borrada");
      onChanged();
      onClose();
    } catch (e) {
      console.error("Error al borrar cita:", e);
      toast.error(e?.response?.data?.detail || "No se pudo borrar la cita");
    }
  };

  const doReschedule = async () => {
    try {
      await api.put(`/appointments/${appt.id}`, { date: reDate, time: reTime });
      toastReschedule("Cita reagendada");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const wa = waLink(
    appt?.patient_phone,
    `Hola ${appt?.patient_name}, le confirmamos su cita con el Dr. Jairo Franco el ${fmtDateLong(
      appt?.date || todayStr()
    )} a las ${appt?.time} hrs. Por favor responda para confirmar su asistencia. ¡Gracias!`
  );

  return (
    <Modal open={open} onClose={onClose} title="Detalle de la cita" testid="appt-detail-modal">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display text-2xl text-[#1F4E78] capitalize">{fmtDateLong(appt.date)}</p>
            <p className="flex items-center gap-1.5 text-muted-foreground"><Clock size={15} /> {appt.time} hrs · {appt.duration} min</p>
          </div>
          <StatusBadge status={appt.status} />
        </div>

        <div className="rounded-xl bg-[#F4F8FC] p-4 space-y-2">
          <p className="flex items-center gap-2 font-semibold text-[#1F4E78]"><UserIcon size={16} /> {appt.patient_name}</p>
          <p className="flex items-center gap-2 text-sm"><Phone size={15} className="text-muted-foreground" /> {appt.patient_phone}</p>
          {appt.patient_email && <p className="text-sm text-muted-foreground pl-6">{appt.patient_email}</p>}
          <div className="flex flex-wrap gap-2 pt-1">
            <CategoriaBadge categoria={appt.categoria} />
            <TipoBadge tipo={appt.tipo} />
            {appt.embarazada && (
              <span className="chip flex items-center gap-1" style={{ background: "#FDE7F0", color: "#9D255E" }}>
                <Baby size={13} /> Embarazada
              </span>
            )}
          </div>
          {appt.motivo && (
            <div className="pt-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Motivo</p>
              <p className="text-sm">{appt.motivo}</p>
            </div>
          )}
        </div>

        <a href={wa} target="_blank" rel="noreferrer"
          data-testid="whatsapp-btn"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold text-white transition-colors"
          style={{ background: "#25D366" }}>
          <MessageCircle size={18} /> Confirmar por WhatsApp
        </a>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Cambiar estado</p>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_ACTIONS.map((s) => (
              <button
                key={s.key}
                data-testid={`set-status-${s.key}`}
                onClick={() => setStatus(s.key)}
                className="py-2 rounded-xl text-sm font-semibold border transition-all hover:shadow-sm"
                style={{
                  background: STATUS_META[s.key].bg,
                  color: STATUS_META[s.key].fg,
                  borderColor: "transparent",
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <button
            onClick={() => setShowReschedule((v) => !v)}
            data-testid="toggle-reschedule"
            className="btn-outline w-full py-2.5 flex items-center justify-center gap-2"
          >
            <RefreshCw size={16} /> Reagendar
          </button>
          {showReschedule && (
            <div className="mt-3 space-y-3 rounded-xl border border-border p-3 animate-fadeup">
              <input type="date" min={todayStr()} value={reDate}
                onChange={(e) => { setReDate(e.target.value); setReTime(null); }}
                className="field" data-testid="staff-reschedule-date" />
              <div className="grid grid-cols-4 gap-2">
                {reSlots.length === 0 && <p className="col-span-4 text-sm text-muted-foreground text-center py-2">Sin horarios.</p>}
                {reSlots.map((s) => (
                  <button key={s} onClick={() => setReTime(s)}
                    className={`py-1.5 rounded-lg text-sm font-semibold border ${reTime === s ? "bg-[#2E6CA4] text-white border-[#2E6CA4]" : "bg-white border-border"}`}>
                    {s}
                  </button>
                ))}
              </div>
              <button disabled={!reTime} onClick={doReschedule} className="btn-primary w-full py-2">Confirmar nuevo horario</button>
            </div>
          )}
        </div>

        {canNotes && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <NotebookPen size={14} /> Notas internas (privadas)
            </p>
            <textarea
              data-testid="internal-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="field min-h-[80px] resize-none"
              placeholder="Solo visible para el personal…"
            />
            <button onClick={saveNotes} disabled={savingNotes} className="btn-ghost mt-2 px-4 py-1.5 text-sm flex items-center gap-1.5">
              {savingNotes ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Guardar nota
            </button>
          </div>
        )}

        {canNotes && (
          <div className="pt-4 border-t border-border">
            <button
              onClick={deleteAppt}
              data-testid="delete-appt-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold border border-[#f3c2c2] text-[#b91c1c] hover:bg-[#fef2f2] transition-colors"
            >
              <Trash2 size={16} /> Borrar cita
            </button>
            <p className="text-[11px] text-center text-muted-foreground mt-1.5">
              Elimina la cita permanentemente para mantener la agenda limpia.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------------- New Appointment ---------------- */
function NewApptModal({ open, onClose, onCreated, prefill }) {
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ patient_name: "", patient_phone: "", patient_email: "", motivo: "", tipo: "primera", categoria: null, embarazada: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/availability", { params: { date } })
      .then((r) => {
        setSlots(r.data.slots);
        setTime((prev) => (prev && r.data.slots.includes(prev) ? prev : null));
      })
      .catch((e) => console.error("Error al cargar horarios:", e));
  }, [date, open]);

  useEffect(() => {
    if (!open) {
      setForm({ patient_name: "", patient_phone: "", patient_email: "", motivo: "", tipo: "primera", categoria: null, embarazada: false });
      setDate(todayStr());
      setTime(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && prefill) {
      if (prefill.date) setDate(prefill.date);
      if (prefill.time) setTime(prefill.time);
      if (prefill.patient_name || prefill.patient_phone) {
        setForm((f) => ({
          ...f,
          patient_name: prefill.patient_name || "",
          patient_phone: prefill.patient_phone || "",
          categoria: "control_prenatal",
          embarazada: true,
          tipo: "subsecuente",
        }));
      }
    }
  }, [open, prefill]);

  const save = async () => {
    if (!form.patient_name.trim() || !form.patient_phone.trim() || !time) {
      toast.error("Completa nombre, teléfono y horario");
      return;
    }
    if (!form.categoria) {
      toast.error("Selecciona el tipo de consulta");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", { date, time, ...form });
      toast.success("Cita creada");
      window.open(
        clinicNotifyLink(form.patient_name, date, time, form.patient_phone, form.tipo),
        "_blank"
      );
      onCreated();
      onClose();
      setForm({ patient_name: "", patient_phone: "", patient_email: "", motivo: "", tipo: "primera", categoria: null, embarazada: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Nueva cita" testid="new-appt-modal">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="field" data-testid="new-appt-date" />
          <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="field">
            <option value="primera">Primera consulta</option>
            <option value="subsecuente">Subsecuente</option>
          </select>
        </div>
        <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
          {slots.length === 0 && <p className="col-span-4 text-sm text-muted-foreground text-center py-2">Sin horarios este día.</p>}
          {slots.map((s) => (
            <button key={s} onClick={() => setTime(s)}
              className={`py-1.5 rounded-lg text-sm font-semibold border ${time === s ? "bg-[#2E6CA4] text-white border-[#2E6CA4]" : "bg-white border-border"}`}>
              {s}
            </button>
          ))}
        </div>
        <input className="field" placeholder="Nombre del paciente *" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="new-appt-name" />
        <input className="field" placeholder="Teléfono *" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} data-testid="new-appt-phone" />
        <input className="field" placeholder="Correo (opcional)" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} />
        <div>
          <label className="text-sm font-medium mb-2 block">Tipo de consulta *</label>
          <div className="grid grid-cols-2 gap-2" data-testid="new-appt-categorias">
            {CATEGORIAS.map((c) => {
              const active = form.categoria === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`new-categoria-${c.key}`}
                  onClick={() => setForm({ ...form, categoria: c.key })}
                  className="py-2.5 px-3 rounded-xl text-sm font-semibold border-2 transition-all text-left flex items-center gap-2"
                  style={{
                    background: active ? c.bg : "#fff",
                    color: active ? c.color : "#475569",
                    borderColor: active ? c.color : "#E2E8F0",
                  }}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <textarea className="field resize-none" placeholder="Motivo (opcional)" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
        <button type="button" onClick={() => setForm({ ...form, embarazada: !form.embarazada })}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-medium ${form.embarazada ? "bg-[#FDE7F0] border-[#E48FB5] text-[#9D255E]" : "bg-white border-border text-muted-foreground"}`}>
          <Baby size={18} /> Embarazada
        </button>
        <button onClick={save} disabled={saving} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} Crear cita
        </button>
      </div>
    </Modal>
  );
}

/* ---------------- Appointment card ---------------- */
function ApptCard({ a, onClick }) {
  const catMeta = a.categoria ? CATEGORIA_META[a.categoria] : null;
  const barColor = catMeta ? catMeta.color : a.tipo === "primera" ? "#2E6CA4" : "#0E7490";
  return (
    <button
      onClick={onClick}
      data-testid={`appt-card-${a.id}`}
      className="w-full text-left card-surface p-4 hover:shadow-md transition-shadow flex items-center gap-3 animate-fadeup"
      style={{ borderLeft: `4px solid ${barColor}` }}
    >
      <div className="text-center min-w-[58px]">
        <p className="font-display text-2xl text-[#1F4E78] leading-none">{a.time}</p>
        <p className="text-[11px] text-muted-foreground mt-1">{a.duration}min</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate flex items-center gap-1.5">
          {a.patient_name}
          {a.embarazada && <Baby size={14} className="text-[#C2477A]" />}
        </p>
        <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={11} /> {a.patient_phone}</p>
        {catMeta && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold mt-1" style={{ color: catMeta.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: catMeta.color }} />
            {catMeta.label}
          </span>
        )}
      </div>
      <StatusBadge status={a.status} />
    </button>
  );
}

function DisponibleRow({ time, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`disponible-${time}`}
      className="w-full text-left p-3 rounded-2xl flex items-center gap-3 transition-all hover:shadow-md animate-fadeup"
      style={{ background: "#EAF8EF", border: "1px solid #A9E3C0" }}
    >
      <div className="text-center min-w-[58px]">
        <p className="font-display text-2xl leading-none" style={{ color: "#15803D" }}>{time}</p>
      </div>
      <div className="flex-1 min-w-0">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wide px-2.5 py-1 rounded-full" style={{ background: "#16A34A", color: "#fff" }}>
          DISPONIBLE
        </span>
        <p className="text-xs mt-1" style={{ color: "#3F8F5F" }}>Toca para agendar una nueva cita</p>
      </div>
      <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0" style={{ background: "#16A34A", color: "#fff" }}>
        <Plus size={17} />
      </span>
    </button>
  );
}

/* ---------------- Agenda ---------------- */
function Agenda({ onOpen, onNewAt, refreshKey }) {
  const [mode, setMode] = useState("dia");
  const [anchor, setAnchor] = useState(todayStr());
  const [appts, setAppts] = useState([]);
  const [daySlots, setDaySlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const range = useCallback(() => {
    if (mode === "dia") return { start: anchor, end: anchor };
    if (mode === "semana") {
      const d = new Date(anchor + "T00:00:00");
      const dow = (d.getDay() + 6) % 7; // mon=0
      const start = addDaysStr(anchor, -dow);
      return { start, end: addDaysStr(start, 6) };
    }
    const d = new Date(anchor + "T00:00:00");
    const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return { start, end: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}` };
  }, [mode, anchor]);

  const load = useCallback(() => {
    const { start, end } = range();
    setLoading(true);
    const reqs = [api.get("/appointments", { params: { start, end } })];
    if (mode === "dia") reqs.push(api.get("/availability", { params: { date: anchor } }));
    Promise.all(reqs)
      .then((res) => {
        setAppts(res[0].data);
        setDaySlots(mode === "dia" && res[1] ? res[1].data.slots : []);
      })
      .catch((e) => console.error("Error al cargar la agenda:", e))
      .finally(() => setLoading(false));
  }, [range, mode, anchor]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Auto-actualización: nuevas citas de pacientes aparecen sin recargar manualmente
  useEffect(() => {
    const id = setInterval(load, 25000);
    const onFocus = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [load]);

  const step = (dir) => {
    if (mode === "dia") setAnchor(addDaysStr(anchor, dir));
    else if (mode === "semana") setAnchor(addDaysStr(anchor, dir * 7));
    else {
      const d = new Date(anchor + "T00:00:00");
      d.setMonth(d.getMonth() + dir);
      setAnchor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`);
    }
  };

  const grouped = appts.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort();

  const dayItems = [
    ...appts.map((a) => ({ kind: "appt", time: a.time, a })),
    ...daySlots.map((t) => ({ kind: "slot", time: t })),
  ].sort((x, y) => x.time.localeCompare(y.time));

  const label = mode === "mes"
    ? new Date(anchor + "T00:00:00").toLocaleDateString("es-MX", { month: "long", year: "numeric" })
    : mode === "semana"
    ? `Semana del ${fmtDateShort(range().start)}`
    : fmtDateLong(anchor);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex p-1 rounded-full bg-[#EDF3F9]">
          {["dia", "semana", "mes"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`agenda-mode-${m}`}
              className={`px-4 py-1.5 text-sm rounded-full font-semibold capitalize transition-all ${mode === m ? "bg-white text-[#2E6CA4] shadow-sm" : "text-muted-foreground"}`}>
              {m === "dia" ? "Día" : m}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => step(-1)} className="p-2 rounded-full hover:bg-muted" data-testid="agenda-prev"><ChevronLeft size={18} /></button>
          <span className="text-sm font-semibold capitalize min-w-[160px] text-center">{label}</span>
          <button onClick={() => step(1)} className="p-2 rounded-full hover:bg-muted" data-testid="agenda-next"><ChevronRight size={18} /></button>
          <button onClick={() => setAnchor(todayStr())} className="btn-ghost px-3 py-1.5 text-sm">Hoy</button>
          <button onClick={load} className="p-2 rounded-full hover:bg-muted text-[#2E6CA4]" data-testid="agenda-refresh" title="Actualizar"><RefreshCw size={17} /></button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>
      ) : mode === "dia" ? (
        dayItems.length === 0 ? (
          <div className="card-surface p-12 text-center text-muted-foreground">
            <CalendarDays className="mx-auto mb-3 opacity-40" size={40} />
            No hay horarios ni citas en este día.
          </div>
        ) : (
          <div className="space-y-2" data-testid="agenda-day-list">
            {dayItems.map((it) =>
              it.kind === "appt" ? (
                <ApptCard key={it.a.id} a={it.a} onClick={() => onOpen(it.a)} />
              ) : (
                <DisponibleRow key={`slot-${it.time}`} time={it.time} onClick={() => onNewAt(anchor, it.time)} />
              )
            )}
          </div>
        )
      ) : dates.length === 0 ? (
        <div className="card-surface p-12 text-center text-muted-foreground">
          <CalendarDays className="mx-auto mb-3 opacity-40" size={40} />
          No hay citas en este periodo.
        </div>
      ) : (
        <div className="space-y-6">
          {dates.map((d) => (
            <div key={d}>
              {mode !== "dia" && (
                <p className="font-display text-xl text-[#1F4E78] capitalize mb-2 sticky top-[64px] bg-background/80 backdrop-blur py-1">
                  {fmtDateLong(d)}
                </p>
              )}
              <div className="space-y-2">
                {grouped[d].sort((x, y) => x.time.localeCompare(y.time)).map((a) => (
                  <ApptCard key={a.id} a={a} onClick={() => onOpen(a)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Monthly Summary ---------------- */
function monthLabel(m) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
}
function shiftMonth(m, delta) {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MonthlySummary() {
  const nowM = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(nowM);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/stats/monthly", { params: { month } })
      .then((r) => setData(r.data))
      .catch((e) => console.error("Error al cargar resumen mensual:", e));
  }, [month]);

  const cats = ["control_prenatal", "ginecologica", "checkup_anual", "climaterio", "otro"];

  return (
    <div className="card-surface p-5" data-testid="monthly-summary">
      <div className="flex items-center justify-between mb-4 gap-2">
        <p className="font-display text-2xl text-[#1F4E78]">Resumen del mes</p>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-2 rounded-full hover:bg-muted" data-testid="month-prev"><ChevronLeft size={18} /></button>
          <span className="text-sm font-semibold capitalize min-w-[130px] text-center">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-2 rounded-full hover:bg-muted" data-testid="month-next"><ChevronRight size={18} /></button>
        </div>
      </div>
      {!data ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>
      ) : (
        <>
          <div className="rounded-2xl p-5 mb-4 text-center" style={{ background: "linear-gradient(135deg,#2E6CA4,#1F4E78)" }}>
            <p className="font-display text-5xl text-white leading-none" data-testid="monthly-total">{data.total}</p>
            <p className="text-white/80 text-sm mt-2">consultas agendadas este mes</p>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Por tipo de consulta</p>
          <div className="space-y-2">
            {cats.map((key) => {
              const meta = CATEGORIA_META[key];
              const count = data.by_categoria?.[key] || 0;
              const pct = data.total > 0 ? Math.round((count / data.total) * 100) : 0;
              if (key === "otro" && count === 0) return null;
              return (
                <div key={key} data-testid={`monthly-cat-${key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium flex items-center gap-2" style={{ color: meta.color }}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                      {meta.label}
                    </span>
                    <span className="font-semibold text-foreground">{count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: meta.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Stats ---------------- */
function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)).catch((e) => console.error("Error al cargar estadísticas:", e)); }, []);
  if (!s) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>;

  const cards = [
    { k: "today", label: "Citas hoy", color: "#2E6CA4" },
    { k: "pendiente", label: "Pendientes", color: "#D97706" },
    { k: "confirmada", label: "Confirmadas", color: "#2563EB" },
    { k: "asistio", label: "Completadas", color: "#16A34A" },
    { k: "cancelada", label: "Canceladas", color: "#DC2626" },
    { k: "no_asistio", label: "No asistió", color: "#6B7280" },
  ];
  const chartData = cards.filter((c) => c.k !== "today").map((c) => ({ name: c.label, value: s[c.k], color: c.color }));

  return (
    <div className="space-y-6">
      <MonthlySummary />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.k} className="card-surface p-5" data-testid={`stat-${c.k}`}>
            <p className="font-display text-4xl" style={{ color: c.color }}>{s[c.k]}</p>
            <p className="text-sm text-muted-foreground mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="card-surface p-5">
        <p className="font-display text-2xl text-[#1F4E78] mb-4">Resumen por estado</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <RTooltip />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
              {chartData.map((e) => <Cell key={e.name} fill={e.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ---------------- Configuración ---------------- */
function Configuracion() {
  const [settings, setSettings] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pay, setPay] = useState(null);
  const [savingPay, setSavingPay] = useState(false);
  const [blockForm, setBlockForm] = useState({ type: "extra", date: todayStr(), start_date: todayStr(), end_date: todayStr(), start_time: "09:00", end_time: "10:00", reason: "" });

  const loadAll = () => {
    api.get("/settings").then((r) => { setSettings(r.data); setPay(r.data.payment_config); }).catch((e) => console.error("Error al cargar configuración:", e));
    api.get("/settings/blocks").then((r) => setBlocks(r.data)).catch((e) => console.error("Error al cargar bloqueos:", e));
  };
  useEffect(() => { loadAll(); }, []);

  if (!settings || !pay) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>;

  const savePay = async () => {
    setSavingPay(true);
    try {
      await api.put("/settings/payment", pay);
      toast.success("Configuración de anticipos guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSavingPay(false);
    }
  };
  const setBank = (k, v) => setPay({ ...pay, bank: { ...pay.bank, [k]: v } });
  const setService = (key, field, v) => setPay({ ...pay, services: { ...pay.services, [key]: { ...pay.services[key], [field]: v } } });


  const windowsByDay = (day) => settings.weekly_availability.filter((w) => w.day === day);

  const addWindow = (day) => {
    setSettings({ ...settings, weekly_availability: [...settings.weekly_availability, { day, start: "09:00", end: "13:00" }] });
  };
  const updateWindow = (idx, field, val) => {
    const wa = settings.weekly_availability.map((w, i) =>
      i === idx ? { ...w, [field]: val } : w
    );
    setSettings({ ...settings, weekly_availability: wa });
  };
  const removeWindow = (idx) => {
    const wa = settings.weekly_availability.filter((_, i) => i !== idx);
    setSettings({ ...settings, weekly_availability: wa });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { slot_duration: settings.slot_duration, weekly_availability: settings.weekly_availability });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    try {
      const payload = { type: blockForm.type, reason: blockForm.reason };
      if (blockForm.type === "day") payload.date = blockForm.date;
      if (blockForm.type === "slot" || blockForm.type === "extra") { payload.date = blockForm.date; payload.start_time = blockForm.start_time; payload.end_time = blockForm.end_time; }
      if (blockForm.type === "vacation") { payload.start_date = blockForm.start_date; payload.end_date = blockForm.end_date; }
      await api.post("/settings/blocks", payload);
      toast.success(blockForm.type === "extra" ? "Horario agregado a ese día" : "Ajuste agregado");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const delBlock = async (id) => {
    await api.delete(`/settings/blocks/${id}`);
    loadAll();
  };

  const blockLabel = (b) => {
    if (b.type === "day") return `Día bloqueado · ${fmtDateShort(b.date)}`;
    if (b.type === "extra") return `Horario agregado · ${fmtDateShort(b.date)} ${b.start_time}-${b.end_time}`;
    if (b.type === "slot") return `Horario quitado · ${fmtDateShort(b.date)} ${b.start_time}-${b.end_time}`;
    return `Vacaciones · ${fmtDateShort(b.start_date)} → ${fmtDateShort(b.end_date)}`;
  };

  // build a flat index map for editing

  return (
    <div className="space-y-6">
      <div className="card-surface p-5" data-testid="payment-config-card">
        <p className="font-display text-2xl text-[#1F4E78] mb-1">Anticipos y datos bancarios</p>
        <p className="text-sm text-muted-foreground mb-4">Configura el anticipo, el tiempo de reserva, la referencia y la cuenta para transferencias. La cuenta se guarda como texto para no perder dígitos.</p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <label className="text-sm">Anticipo (MXN)
            <input type="number" data-testid="cfg-deposit" className="field mt-1" value={pay.deposit_amount} onChange={(e) => setPay({ ...pay, deposit_amount: Number(e.target.value) })} />
          </label>
          <label className="text-sm">Minutos de reserva
            <input type="number" data-testid="cfg-reservation-min" className="field mt-1" value={pay.reservation_minutes} onChange={(e) => setPay({ ...pay, reservation_minutes: Number(e.target.value) })} />
          </label>
          <label className="text-sm">Horas mín. reembolso
            <input type="number" data-testid="cfg-min-hours" className="field mt-1" value={pay.min_hours_refund} onChange={(e) => setPay({ ...pay, min_hours_refund: Number(e.target.value) })} />
          </label>
          <label className="text-sm">Prefijo de folio
            <input className="field mt-1" value={pay.folio_prefix} onChange={(e) => setPay({ ...pay, folio_prefix: e.target.value })} />
          </label>
          <label className="text-sm">Longitud referencia
            <input type="number" className="field mt-1" value={pay.reference_length} onChange={(e) => setPay({ ...pay, reference_length: Number(e.target.value) })} />
          </label>
          <label className="text-sm">WhatsApp para comprobante
            <input data-testid="cfg-comprobante-wa" className="field mt-1" value={pay.comprobante_whatsapp} onChange={(e) => setPay({ ...pay, comprobante_whatsapp: e.target.value })} />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <label className="text-sm">Banco
            <input className="field mt-1" value={pay.bank.banco} onChange={(e) => setBank("banco", e.target.value)} />
          </label>
          <label className="text-sm">Titular
            <input className="field mt-1" value={pay.bank.titular} onChange={(e) => setBank("titular", e.target.value)} />
          </label>
          <label className="text-sm">Cuenta / CLABE (texto)
            <input data-testid="cfg-bank-cuenta" className="field mt-1" value={pay.bank.cuenta} onChange={(e) => setBank("cuenta", e.target.value.replace(/[^0-9]/g, ""))} />
          </label>
          <label className="text-sm">Concepto
            <input className="field mt-1" value={pay.bank.concepto} onChange={(e) => setBank("concepto", e.target.value)} />
          </label>
        </div>
        <p className="text-sm font-semibold text-[#1F4E78] mb-2">Servicios (precio y anticipo)</p>
        <div className="space-y-2 mb-4">
          {Object.entries(pay.services).map(([key, sv]) => (
            <div key={key} className="rounded-xl border border-border p-3" data-testid={`service-${key}`}>
              <p className="font-semibold text-sm mb-2">{sv.label}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <label className="text-xs">Precio total
                  <input type="number" data-testid={`service-${key}-price`} className="field mt-1" value={sv.price} onChange={(e) => setService(key, "price", Number(e.target.value))} />
                </label>
                <label className="text-xs">Anticipo
                  <input type="number" data-testid={`service-${key}-deposit`} className="field mt-1" value={sv.deposit} onChange={(e) => setService(key, "deposit", Number(e.target.value))} />
                </label>
                <label className="text-xs">Modalidad
                  <select className="field mt-1" value={sv.modalidad} onChange={(e) => setService(key, "modalidad", e.target.value)}>
                    <option value="presencial">Presencial</option>
                    <option value="linea">En línea</option>
                    <option value="ambas">Ambas</option>
                  </select>
                </label>
                <label className="text-xs flex items-center gap-2 mt-5">
                  <input type="checkbox" checked={sv.active} onChange={(e) => setService(key, "active", e.target.checked)} className="w-4 h-4 accent-[#2E6CA4]" />
                  Activo
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Saldo al agendar: ${Math.max(0, (sv.price || 0) - (sv.deposit || 0))} MXN</p>
            </div>
          ))}
        </div>
        <button onClick={savePay} disabled={savingPay} className="btn-primary px-6 py-2.5 flex items-center gap-2" data-testid="save-payment-config">
          {savingPay ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Guardar anticipos
        </button>
      </div>

      <div className="card-surface p-5">
        <p className="font-display text-2xl text-[#1F4E78] mb-4">Duración de la consulta</p>
        <div className="flex flex-wrap gap-2">
          {DURATIONS.map((d) => (
            <button key={d} data-testid={`duration-${d}`}
              onClick={() => setSettings({ ...settings, slot_duration: d })}
              className={`px-4 py-2 rounded-full font-semibold text-sm border transition-all ${settings.slot_duration === d ? "bg-[#2E6CA4] text-white border-[#2E6CA4]" : "bg-white border-border"}`}>
              {d} min
            </button>
          ))}
        </div>
      </div>

      <div className="card-surface p-5">
        <p className="font-display text-2xl text-[#1F4E78] mb-1">Horario semanal disponible</p>
        <p className="text-sm text-muted-foreground mb-4">Define los bloques de atención por día. El sistema genera los turnos automáticamente.</p>
        <div className="space-y-4">
          {DAYS.map((dayName, dayIdx) => (
            <div key={dayIdx} className="border-b border-border pb-3 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold">{dayName}</p>
                <button onClick={() => addWindow(dayIdx)} className="btn-ghost px-3 py-1 text-sm flex items-center gap-1" data-testid={`add-window-${dayIdx}`}>
                  <Plus size={14} /> Bloque
                </button>
              </div>
              <div className="space-y-2">
                {settings.weekly_availability.map((w, idx) => {
                  if (w.day !== dayIdx) return null;
                  return (
                    <div key={`${w.day}-${w.start}-${w.end}-${idx}`} className="flex items-center gap-2">
                      <input type="time" value={w.start} onChange={(e) => updateWindow(idx, "start", e.target.value)} className="field py-1.5 max-w-[130px]" />
                      <span className="text-muted-foreground">a</span>
                      <input type="time" value={w.end} onChange={(e) => updateWindow(idx, "end", e.target.value)} className="field py-1.5 max-w-[130px]" />
                      <button onClick={() => removeWindow(idx)} className="p-1.5 text-[#b91c1c] hover:bg-[#fef2f2] rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  );
                })}
                {windowsByDay(dayIdx).length === 0 && <p className="text-xs text-muted-foreground">Sin atención.</p>}
              </div>
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="btn-primary mt-5 px-6 py-2.5 flex items-center gap-2" data-testid="save-settings">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Guardar horario
        </button>
      </div>

      <div className="card-surface p-5">
        <p className="font-display text-2xl text-[#1F4E78] mb-1 flex items-center gap-2"><CalendarOff size={20} /> Ajustes por día y ausencias</p>
        <p className="text-sm text-muted-foreground mb-4">Agrega o quita horarios en un día específico (sin afectar el resto de la semana), bloquea días completos o registra vacaciones.</p>
        <div className="rounded-xl border border-border p-3 space-y-3 mb-4">
          <select value={blockForm.type} onChange={(e) => setBlockForm({ ...blockForm, type: e.target.value })} className="field" data-testid="block-type">
            <option value="extra">Agregar horario (un día)</option>
            <option value="slot">Quitar horario (un día)</option>
            <option value="day">Bloquear día completo</option>
            <option value="vacation">Vacaciones / ausencia</option>
          </select>
          {blockForm.type === "day" && (
            <input type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} className="field" data-testid="block-day-date" />
          )}
          {(blockForm.type === "slot" || blockForm.type === "extra") && (
            <div className="flex flex-wrap gap-2">
              <input type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} className="field flex-1 min-w-[140px]" data-testid="block-slot-date" />
              <input type="time" value={blockForm.start_time} onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })} className="field max-w-[120px]" data-testid="block-start-time" />
              <input type="time" value={blockForm.end_time} onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })} className="field max-w-[120px]" data-testid="block-end-time" />
            </div>
          )}
          {blockForm.type === "vacation" && (
            <div className="flex flex-wrap gap-2">
              <input type="date" value={blockForm.start_date} onChange={(e) => setBlockForm({ ...blockForm, start_date: e.target.value })} className="field flex-1 min-w-[140px]" />
              <input type="date" value={blockForm.end_date} onChange={(e) => setBlockForm({ ...blockForm, end_date: e.target.value })} className="field flex-1 min-w-[140px]" />
            </div>
          )}
          <input placeholder="Motivo (opcional)" value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} className="field" />
          <button onClick={addBlock} className="btn-outline px-4 py-2 flex items-center gap-1.5 text-sm" data-testid="add-block">
            <Plus size={15} /> {blockForm.type === "extra" ? "Agregar horario" : blockForm.type === "slot" ? "Quitar horario" : "Agregar"}
          </button>
        </div>
        <div className="space-y-2">
          {blocks.length === 0 && <p className="text-sm text-muted-foreground">Sin bloqueos.</p>}
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg bg-[#F4F8FC] px-3 py-2">
              <div>
                <p className="text-sm font-medium">{blockLabel(b)}</p>
                {b.reason && <p className="text-xs text-muted-foreground">{b.reason}</p>}
              </div>
              <button onClick={() => delBlock(b.id)} className="p-1.5 text-[#b91c1c] hover:bg-white rounded-lg"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Usuarios ---------------- */
function Usuarios() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => console.error("Error al cargar usuarios:", e));
  useEffect(() => { load(); }, []);

  const setRole = async (uid, role) => {
    await api.put(`/users/${uid}/role`, null, { params: { role } });
    toast.success("Rol actualizado");
    load();
  };

  const filtered = users.filter((u) => (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="card-surface p-4 flex items-center gap-2">
        <Search size={18} className="text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario…" className="flex-1 outline-none bg-transparent" data-testid="user-search" />
      </div>
      {filtered.map((u) => (
        <div key={u.user_id} className="card-surface p-4 flex items-center justify-between gap-3" data-testid={`user-${u.user_id}`}>
          <div className="min-w-0">
            <p className="font-semibold truncate">{u.name || "—"}</p>
            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
            <span className="chip mt-1 inline-block" style={{ background: "#E8F0F8", color: "#2E6CA4" }}>{ROLE_LABEL[u.role]}</span>
          </div>
          <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} className="field max-w-[150px] py-1.5" data-testid={`role-select-${u.user_id}`}>
            <option value="paciente">Paciente</option>
            <option value="recepcion">Recepción</option>
            <option value="medico">Médico</option>
          </select>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Notifications ---------------- */
function Notifications({ open, onClose }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (open) {
      api.get("/notifications").then((r) => setItems(r.data)).catch((e) => console.error("Error al cargar notificaciones:", e));
      api.put("/notifications/read-all").catch((error) => console.error("No se pudieron marcar notificaciones como leídas:", error));
    }
  }, [open]);
  if (!open) return null;
  return (
    <div className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto card-surface p-2 z-50 animate-fadeup" data-testid="notifications-panel">
      <div className="flex items-center justify-between px-2 py-1.5">
        <p className="font-semibold text-sm">Notificaciones</p>
        <button onClick={onClose}><X size={16} /></button>
      </div>
      {items.length === 0 && <p className="text-sm text-muted-foreground p-3 text-center">Sin notificaciones.</p>}
      {items.map((n) => (
        <div key={n.id} className="px-3 py-2 rounded-lg hover:bg-muted text-sm">
          <p>{n.text}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString("es-MX")}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Recordatorios ---------------- */
function ReminderRow({ a, when, onSent }) {
  const sent = when === "prev" ? a.reminder_prev_sent : a.reminder_day_sent;
  const send = () => {
    window.open(waLink(a.patient_phone, reminderMsg(a.patient_name, a.date, a.time, when)), "_blank");
    api.put(`/appointments/${a.id}/mark-sent`, null, { params: { kind: when } })
      .then(() => onSent())
      .catch((error) => console.error("No se pudo marcar el recordatorio como enviado:", error));
  };
  return (
    <div className="card-surface p-4 flex items-center gap-3" data-testid={`reminder-row-${a.id}`}>
      <div className="text-center min-w-[52px]">
        <p className="font-display text-xl text-[#1F4E78] leading-none">{a.time}</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate flex items-center gap-1.5">
          {a.patient_name}
          {a.embarazada && <Baby size={13} className="text-[#C2477A]" />}
        </p>
        <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={11} /> {a.patient_phone}</p>
      </div>
      <button
        onClick={send}
        data-testid={`send-reminder-${when}-${a.id}`}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full font-semibold text-sm text-white transition-colors"
        style={{ background: sent ? "#94A3B8" : "#25D366" }}
      >
        {sent ? <CheckCircle2 size={15} /> : <Send size={15} />}
        {sent ? "Reenviar" : "Recordar"}
      </button>
    </div>
  );
}

function Recordatorios() {
  const [data, setData] = useState({ today: [], tomorrow: [] });
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/reminders").then((r) => setData(r.data)).catch((e) => console.error("Error al cargar recordatorios:", e)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>;

  const Group = ({ title, subtitle, items, when }) => (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-1">
        <p className="font-display text-2xl text-[#1F4E78]">{title}</p>
        <span className="chip" style={{ background: "#E8F0F8", color: "#2E6CA4" }}>{items.length} citas</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">Sin citas.</p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => <ReminderRow key={a.id} a={a} when={when} onSent={load} />)}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-[#E8F4EC] border border-[#bfe3cc] p-4 flex items-start gap-3">
        <MessageCircle size={20} className="text-[#1a8f4c] mt-0.5" />
        <p className="text-sm text-[#176b3b]">
          Toca <strong>Recordar</strong> para abrir WhatsApp con el mensaje listo para enviar a la paciente.
          Las citas marcadas en gris ya tienen recordatorio enviado.
        </p>
      </div>
      <div className="card-surface p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Bell size={20} className="text-[#2E6CA4] mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Activa las <strong className="text-foreground">notificaciones pop-up</strong> en este teléfono
            para recibir un aviso cada vez que se agende una cita, aunque la app esté cerrada.
          </p>
        </div>
        <div className="shrink-0">
          <NotificationToggle variant="ghost" />
        </div>
      </div>
      <Group title="Hoy" subtitle="Recordatorio el día de la cita" items={data.today} when="day" />
      <Group title="Mañana" subtitle="Recordatorio un día antes" items={data.tomorrow} when="prev" />
    </div>
  );
}

/* ---------------- Seguimiento prenatal (aviso 25 días) ---------------- */
function PrenatalPending({ refreshKey, onSchedule }) {
  const { user } = useAuth();
  const isMedico = user.role === "medico";
  const [items, setItems] = useState([]);
  const notifiedRef = React.useRef(false);

  const load = useCallback(() => {
    api
      .get("/prenatal/pending")
      .then((r) => {
        const list = r.data || [];
        setItems(list);
        if (list.length > 0 && !notifiedRef.current) {
          notifiedRef.current = true;
          toastPrenatal(`${list.length} paciente(s) requieren agendar su siguiente control prenatal`);
        }
      })
      .catch((e) => console.error("No se pudo cargar seguimiento prenatal:", e));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const dismiss = async (it) => {
    if (!window.confirm(`¿Eliminar el aviso de ${it.patient_name || "esta paciente"}? Reaparecerá solo si tiene un nuevo control prenatal.`)) return;
    try {
      await api.post("/prenatal/dismiss", { key: it.key, last_date: it.last_date });
      setItems((prev) => prev.filter((x) => !(x.key === it.key && x.last_date === it.last_date)));
      toast.success("Aviso eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el aviso");
    }
  };

  if (items.length === 0) return null;

  return (
    <div
      data-testid="prenatal-pending-card"
      className="mb-6 rounded-2xl p-5 animate-fadeup"
      style={{ background: "#FDE7F0", border: "1px solid #F0A8C6" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Baby size={20} style={{ color: "#DB2777" }} />
        <p className="font-display text-xl" style={{ color: "#9D255E" }}>
          Seguimiento prenatal pendiente
        </p>
        <span className="chip ml-auto" style={{ background: "#FBCFE2", color: "#9D255E" }}>
          {items.length}
        </span>
      </div>
      <p className="text-sm mb-4" style={{ color: "#B84A78" }}>
        Estas pacientes tuvieron su último control prenatal hace 25 días o más y no tienen una próxima cita agendada.
      </p>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div
            key={`${it.patient_phone || it.patient_name}-${idx}`}
            data-testid={`prenatal-row-${idx}`}
            className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 bg-white/70 rounded-xl px-3 py-2.5"
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate" style={{ color: "#7A1E48" }}>{it.patient_name || "Paciente"}</p>
              <p className="text-xs flex items-center gap-2" style={{ color: "#B84A78" }}>
                <span className="flex items-center gap-1"><Phone size={11} /> {it.patient_phone || "—"}</span>
                <span>· hace {it.days_since} días</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <button
                data-testid={`prenatal-whatsapp-${idx}`}
                onClick={() => window.open(prenatalInviteLink(it.patient_name, it.patient_phone), "_blank")}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full font-semibold text-sm text-white transition-colors"
                style={{ background: "#25D366" }}
                title="Invitar por WhatsApp a agendar"
              >
                <MessageCircle size={15} /> WhatsApp
              </button>
              <button
                data-testid={`prenatal-schedule-${idx}`}
                onClick={() => onSchedule(it)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full font-semibold text-sm text-white transition-colors"
                style={{ background: "#DB2777" }}
              >
                <Plus size={15} /> Agendar
              </button>
              {isMedico && (
                <button
                  data-testid={`prenatal-dismiss-${idx}`}
                  onClick={() => dismiss(it)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full font-semibold text-sm transition-colors border"
                  style={{ background: "#fff", color: "#B91C1C", borderColor: "#F3C2C2" }}
                  title="Descartar aviso (solo médico)"
                >
                  <Trash2 size={15} /> Descartar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Anticipos / Pagos pendientes (Fase 3) ---------------- */
const PAY_STATUS_META = {
  pendiente_anticipo: { label: "Pendiente de anticipo", bg: "#FFF4E5", color: "#B45309", dot: "#F59E0B" },
  pago_en_revision: { label: "Pago en revisión", bg: "#E7F0FB", color: "#1D4ED8", dot: "#3B82F6" },
};

function AnticiposPanel({ refreshKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmedAppt, setConfirmedAppt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/payments/pending").then((r) => setItems(r.data || [])).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const viewComprobante = async (it) => {
    try {
      const res = await api.get(`/appointments/${it.id}/comprobante`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("No se pudo abrir el comprobante");
    }
  };
  const confirm = async (it) => {
    if (!window.confirm(`¿Confirmar el pago de ${it.patient_name}? La cita quedará CONFIRMADA.`)) return;
    try {
      await api.put(`/appointments/${it.id}/confirm-payment`);
      toastReschedule("Pago confirmado · cita confirmada");
      setConfirmedAppt(it);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const reject = async (it) => {
    const reason = window.prompt("Motivo del rechazo (opcional):", "");
    if (reason === null) return;
    try {
      await api.put(`/appointments/${it.id}/reject-payment`, { reason });
      toastCancel("Pago rechazado");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <p className="font-display text-3xl text-[#1F4E78] mb-1">Anticipos / Pagos pendientes</p>
      <p className="text-sm text-muted-foreground mb-5">Revisa los comprobantes y confirma o rechaza el anticipo. La cita solo queda confirmada cuando confirmas el pago.</p>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2E6CA4]" /></div>
      ) : items.length === 0 ? (
        <div className="card-surface p-12 text-center text-muted-foreground">
          <Receipt className="mx-auto mb-3 opacity-40" size={40} /> No hay anticipos pendientes.
        </div>
      ) : (
        <div className="space-y-3" data-testid="anticipos-list">
          {items.map((it) => {
            const meta = PAY_STATUS_META[it.status] || { label: it.status, bg: "#eee", color: "#333", dot: "#999" };
            return (
              <div key={it.id} className="card-surface p-4" data-testid={`anticipo-${it.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold text-[#1F4E78]">{it.folio}</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                    </div>
                    <p className="font-semibold mt-1">{it.patient_name}</p>
                    <p className="text-xs text-muted-foreground">{it.service_label || it.categoria} · {fmtDateLong(it.date)} · {it.time} hrs</p>
                    <p className="text-xs mt-1">Ref: <span className="font-mono font-semibold">{it.payment_reference}</span> · Anticipo <b>${it.deposit_amount}</b> · Saldo ${it.remaining_amount}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {it.receipt_file_id && (
                      <button data-testid={`view-comprobante-${it.id}`} onClick={() => viewComprobante(it)} className="btn-outline px-3 py-2 text-sm flex items-center gap-1.5"><Receipt size={14} /> Comprobante</button>
                    )}
                    <button data-testid={`confirm-payment-${it.id}`} onClick={() => confirm(it)} className="px-3 py-2 rounded-full text-sm font-semibold text-white flex items-center gap-1.5" style={{ background: "#16A34A" }}><CheckCircle2 size={14} /> Confirmar</button>
                    <button data-testid={`reject-payment-${it.id}`} onClick={() => reject(it)} className="px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 border" style={{ color: "#B91C1C", borderColor: "#F3C2C2" }}><X size={14} /> Rechazar</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!confirmedAppt} onClose={() => setConfirmedAppt(null)} title="¡Cita confirmada!">
        {confirmedAppt && (
          <div className="space-y-3 text-center" data-testid="confirm-wa-modal">
            <CheckCircle2 className="mx-auto text-green-600" size={40} />
            <p className="text-sm text-muted-foreground">
              El pago de <b>{confirmedAppt.patient_name}</b> fue confirmado y la cita <b>{confirmedAppt.folio}</b> quedó registrada.
            </p>
            <p className="text-sm text-muted-foreground">Envía la confirmación a la paciente por WhatsApp:</p>
            <a
              href={paymentConfirmedLink(confirmedAppt)}
              target="_blank"
              rel="noreferrer"
              data-testid="send-confirmation-wa"
              onClick={() => setConfirmedAppt(null)}
              className="w-full py-3 rounded-full font-semibold text-white flex items-center justify-center gap-2"
              style={{ background: "#25D366" }}
            >
              <MessageCircle size={18} /> Enviar confirmación por WhatsApp
            </a>
            <button onClick={() => setConfirmedAppt(null)} className="text-sm text-muted-foreground mt-1">Ahora no</button>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Main Dashboard ---------------- */
export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const isMedico = user.role === "medico";
  const [section, setSection] = useState("agenda");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [showNotif, setShowNotif] = useState(false);
  const [unread, setUnread] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const poll = () => {
      api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch((error) => console.error("No se pudieron obtener las notificaciones:", error));
      api.get("/payments/pending").then((r) => setReviewCount((r.data || []).filter((p) => p.status === "pago_en_revision").length)).catch((e) => console.error("No se pudo obtener anticipos:", e));
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [refreshKey, showNotif]);

  const navBadge = (key) => (key === "anticipos" ? reviewCount : 0);

  const nav = [
    { key: "agenda", label: "Agenda", icon: CalendarDays, show: true },
    { key: "anticipos", label: "Anticipos", icon: Receipt, show: true },
    { key: "recordatorios", label: "Recordatorios", icon: AlarmClock, show: true },
    { key: "stats", label: "Estadísticas", icon: BarChart3, show: isMedico },
    { key: "config", label: "Configuración", icon: Settings, show: isMedico },
    { key: "usuarios", label: "Usuarios", icon: Users, show: isMedico },
  ].filter((n) => n.show);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-white/60 backdrop-blur p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 mb-8">
          <Logo size={42} />
          <div>
            <p className="font-display text-xl leading-none text-[#1F4E78]">Dr. Jairo Franco</p>
            <p className="text-[11px] text-muted-foreground">{ROLE_LABEL[user.role]}</p>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {nav.map((n) => (
            <button key={n.key} onClick={() => setSection(n.key)} data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium transition-all ${section === n.key ? "bg-[#2E6CA4] text-white shadow-sm" : "hover:bg-muted text-foreground"}`}>
              <n.icon size={18} /> {n.label}
              {navBadge(n.key) > 0 && (
                <span data-testid={`nav-badge-${n.key}`} className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                  {navBadge(n.key)}
                </span>
              )}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-muted text-muted-foreground" data-testid="logout-btn">
          <LogOut size={18} /> Cerrar sesión
        </button>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-border">
          <div className="px-4 lg:px-8 py-3 flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <Logo size={34} />
              <p className="font-display text-lg text-[#1F4E78]">Dr. Jairo Franco</p>
            </div>
            <h1 className="hidden lg:block font-display text-3xl text-[#1F4E78] capitalize">
              {nav.find((n) => n.key === section)?.label}
            </h1>
            <div className="flex items-center gap-2 relative">
              <NotificationToggle variant="ghost" />
              <InstallPWA variant="ghost" className="hidden md:flex" />
              <button onClick={() => setShowNew(true)} className="btn-primary px-4 py-2 text-sm flex items-center gap-1.5" data-testid="new-appt-btn">
                <Plus size={16} /> <span className="hidden sm:inline">Nueva cita</span>
              </button>
              <button onClick={() => setShowNotif((v) => !v)} className="relative p-2 rounded-full hover:bg-muted" data-testid="notif-btn">
                <Bell size={20} />
                {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 text-[10px] flex items-center justify-center rounded-full bg-[#DC2626] text-white">{unread}</span>}
              </button>
              <Notifications open={showNotif} onClose={() => setShowNotif(false)} />
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-6 pb-24 lg:pb-8 max-w-4xl w-full mx-auto">
          {section === "agenda" && (
            <PrenatalPending
              refreshKey={refreshKey}
              onSchedule={(patient) => {
                setPrefill(patient);
                setShowNew(true);
              }}
            />
          )}
          {section === "agenda" && (
            <Agenda
              onOpen={setSelected}
              onNewAt={(date, time) => {
                setPrefill({ date, time });
                setShowNew(true);
              }}
              refreshKey={refreshKey}
            />
          )}
          {section === "anticipos" && <AnticiposPanel refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />}
          {section === "recordatorios" && <Recordatorios />}          {section === "stats" && isMedico && <Stats />}
          {section === "config" && isMedico && <Configuracion />}
          {section === "usuarios" && isMedico && <Usuarios />}
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-border flex z-30">
        {nav.map((n) => (
          <button key={n.key} onClick={() => setSection(n.key)} data-testid={`bottomnav-${n.key}`}
            className={`relative flex-1 flex flex-col items-center py-2.5 gap-0.5 text-[11px] font-medium ${section === n.key ? "text-[#2E6CA4]" : "text-muted-foreground"}`}>
            <n.icon size={20} />
            {navBadge(n.key) > 0 && (
              <span data-testid={`bottomnav-badge-${n.key}`} className="absolute top-1 right-1/2 translate-x-4 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {navBadge(n.key)}
              </span>
            )}
            {n.label}
          </button>
        ))}
        <button onClick={logout} className="flex-1 flex flex-col items-center py-2.5 gap-0.5 text-[11px] font-medium text-muted-foreground">
          <LogOut size={20} /> Salir
        </button>
      </nav>

      {selected && (
        <ApptDetail
          appt={selected}
          open={!!selected}
          onClose={() => setSelected(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          canNotes={true}
        />
      )}
      <NewApptModal
        open={showNew}
        onClose={() => {
          setShowNew(false);
          setPrefill(null);
        }}
        onCreated={() => setRefreshKey((k) => k + 1)}
        prefill={prefill}
      />
    </div>
  );
}
